import React, { useState } from 'react';
import { IconName, SvgIcon } from '@adjust/dashboard-components';
import { Checkbox } from '../Common/Checkbox';
import { Field, FieldProps } from './Field';
import { Input } from './Input';
import { Info } from './Info';

type FieldPathProps = Pick<FieldProps, 'form' | 'isReadOnly' | 'label' | 'labelAside'> & {
  name: 'iosUniversalLinkPath' | 'androidDeeplinkPath' | 'commonDeeplinkPath';
  enforceName: 'iosUniversalLinkPathEnforce' | 'androidDeeplinkPathEnforce' | 'commonDeeplinkPathEnforce';
  onTrackEvent: (name: string) => void;
};

const NOTE =
  'Add just the path from your deeplink. For example, "reward_page" is the path from "my_app://reward_page".';

export const FieldPath: React.FC<FieldPathProps> = ({
  form,
  name,
  enforceName,
  isReadOnly = false,
  onTrackEvent,
  ...props
}) => {
  const [isInstructionsVisible, setIsInstructionsVisible] = useState(false);

  const handleEnforceChange = () => {
    form.trigger(name);
    onTrackEvent('checkbox_enforce');
  };

  const showInstructions = () => {
    setIsInstructionsVisible(true);
    onTrackEvent('help_validate_path');
  };

  return (
    <div className="form-row form-row-group flex-box-row-wrap">
      <Field form={form} name={name} isReadOnly={isReadOnly} {...props} component={<Input />} />

      {!isReadOnly && (
        <>
          <div className="form-row-checkbox">
            <Checkbox ref={form.register} name={enforceName} label="enforce" onChange={handleEnforceChange} />
          </div>

          <Info>
            {NOTE}{' '}
            <a href="#" onClick={showInstructions}>
              This is how we validate your path.
            </a>
          </Info>
        </>
      )}

      {isInstructionsVisible && (
        <div className="modal">
          <div className="modal-content">
            <h2>This is how we validate your path:</h2>

            <button type="button" className="close" onClick={() => setIsInstructionsVisible(false)}>
              <SvgIcon name={IconName.Times} />
            </button>

            <p>Accepted deeplink and universal link path patterns:</p>
            <ul>
              <li>- my_app://mypath</li>
              <li>- my_app://mypath?parameters</li>
              <li>- my_app://?parameters</li>
            </ul>

            <p>
              Accepted <strong>path</strong> and <strong>parameter</strong> syntax:
            </p>

            <ul>
              <li>
                - Paths with alpha-numeric characters and <strong>- / . [ ] %</strong>
              </li>
              <li>
                <p>
                  - Parameter query strings where the key-value pairs are connected with <strong>&</strong>
                </p>
                <ul>
                  <li>
                    - Keys with alpha-numeric characters and <strong>-</strong>
                  </li>
                  <li>
                    - Values with alpha-numeric characters and <strong>- . %</strong>
                  </li>
                </ul>
              </li>
            </ul>

            <p>
              <strong>Example:</strong> <span className="monospace-text">my_app://mypath?key1=value1&key2=value2</span>
            </p>
            <p>
              <strong>Note:</strong> Check <strong>enforce</strong> to override this format and generate a link using a
              non-conforming path pattern. Be warned this may cause issues with your campaign.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
